import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconBookmark, IconLock, IconSearch, IconUserPlus, IconUserCheck } from '@tabler/icons-react';
import { Skeleton } from '@/components/Skeleton';
import { Button } from '@/components/Button';
import { Avatar, displayName } from '@/components/Avatar';
import { api } from '@/api/endpoints';
import { useAuth } from '@/store/auth';
import { useUI } from '@/store/ui';
import { tg } from '@/lib/telegram';
import { trackEvent } from '@/lib/analytics';
import type { FriendItem, UserSearchItem } from '@/api/types';

const BOT_USERNAME = import.meta.env.VITE_TELEGRAM_BOT_USERNAME ?? 'lura_app_bot';

export default function Friends() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const user = useAuth(s => s.user);
  const showToast = useUI(s => s.showToast);
  const [friends, setFriends] = useState<FriendItem[] | null>(null);
  const [invitedByMe, setInvitedByMe] = useState(0);
  const [referralsCount, setReferralsCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSearchItem[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState<Set<number>>(new Set());

  useEffect(() => {
    void api.friends.list().then(({ data, meta }) => {
      setFriends(data);
      const m = meta as { total?: number; invited_by_me?: number; referrals_count?: number } | undefined;
      setTotal(m?.total ?? data.length);
      setInvitedByMe(m?.invited_by_me ?? 0);
      setReferralsCount(m?.referrals_count ?? 0);
    });
  }, []);

  // Debounce поиска: ждём 300мс простоя, потом дёргаем search.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      api.users.search(q)
        .then(({ data }) => setResults(data))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const addFriend = async (u: UserSearchItem) => {
    trackEvent('tap', { element: 'add_friend', user_id: u.id });
    if (adding.has(u.id) || u.is_friend || u.has_pending) return;
    setAdding(prev => new Set(prev).add(u.id));
    try {
      const { data } = await api.friends.add(u.id);
      const accepted = data.status === 'accepted';
      setResults(prev => prev?.map(r => r.id === u.id
        ? { ...r, is_friend: accepted, has_pending: !accepted }
        : r,
      ) ?? null);
      if (accepted) {
        setFriends(prev => prev ? [
          {
            friendship_id: data.friendship_id,
            status: 'accepted',
            created_at: new Date().toISOString(),
            invited_by_me: true,
            is_my_referral: false,
            user: {
              id: u.id,
              first_name: u.first_name,
              username: u.username,
              photo_url: u.photo_url,
              shelf_visibility: 'all',
            },
          },
          ...prev,
        ] : prev);
        setTotal(n => n + 1);
        setInvitedByMe(n => n + 1);
        showToast(t('friends.added_toast', { defaultValue: 'Добавлен' }), 'success');
      } else {
        showToast(t('friends.request_sent_toast', { defaultValue: 'Запрос отправлен' }), 'success');
      }
    } catch {
      showToast(t('errors.generic'), 'error');
    } finally {
      setAdding(prev => { const n = new Set(prev); n.delete(u.id); return n; });
    }
  };

  const friendLink = user?.ref_code
    ? `https://t.me/${BOT_USERNAME}?startapp=friend_${user.ref_code}`
    : null;

  const shareFriend = () => {
    if (!friendLink) return;
    const text = t('friends.invite_share_text');
    tg()?.openTelegramLink(
      `https://t.me/share/url?url=${encodeURIComponent(friendLink)}&text=${encodeURIComponent(text)}`,
    );
  };

  const copyFriend = async () => {
    if (!friendLink) return;
    try {
      await navigator.clipboard.writeText(friendLink);
      showToast(t('common.copied'), 'success');
    } catch {
      showToast(t('common.copy_failed'), 'error');
    }
  };

  return (
    <div className="flex-1 flex flex-col bg-bg min-h-0">
      <header className="px-4 pt-4 pb-3 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-medium">{t('friends.title')}</h1>
          <p className="text-xs text-text-muted mt-0.5">
            {t('friends.summary_with_refs', { a: total, b: invitedByMe, c: referralsCount })}
          </p>
        </div>
        <Button size="sm" onClick={shareFriend} disabled={!friendLink}>
          {t('friends.invite_btn')}
        </Button>
      </header>

      {friendLink && (
        <section className="mx-4 mb-3">
          <button
            onClick={copyFriend}
            className="w-full bg-surface rounded-md p-3 text-xs text-text-muted truncate text-left"
          >
            {friendLink}
          </button>
        </section>
      )}

      <section className="mx-4 mb-3 relative">
        <IconSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
        <input
          type="search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={t('friends.search_placeholder', { defaultValue: 'Поиск по нику' })}
          className="w-full bg-surface rounded-pill pl-9 pr-3 py-2 text-sm placeholder:text-text-muted outline-none"
        />
      </section>

      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
        {query.trim().length >= 2 ? (
          searching ? (
            <Skeleton height={64} />
          ) : results === null || results.length === 0 ? (
            <div className="text-text-muted text-center py-12 text-sm">{t('friends.search_empty', { defaultValue: 'Никого не нашлось' })}</div>
          ) : (
            results.map(u => (
              <div key={u.id} className="w-full p-3 bg-surface rounded-xl flex items-center gap-3">
                <Avatar user={u} size="md" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{displayName(u)}</div>
                </div>
                <button
                  onClick={() => void addFriend(u)}
                  disabled={u.is_friend || u.has_pending || adding.has(u.id)}
                  className={`w-9 h-9 grid place-items-center rounded-pill ${u.is_friend || u.has_pending ? 'bg-surface text-text-muted' : 'bg-accent text-text-inverse'} disabled:opacity-60`}
                  aria-label={
                    u.is_friend
                      ? t('friends.already_added', { defaultValue: 'Уже в друзьях' })
                      : u.has_pending
                        ? t('friends.request_pending', { defaultValue: 'Запрос отправлен' })
                        : t('friends.add_btn', { defaultValue: 'Добавить' })
                  }
                >
                  {u.is_friend || u.has_pending ? <IconUserCheck size={16} /> : <IconUserPlus size={16} />}
                </button>
              </div>
            ))
          )
        ) : friends === null ? (
          <Skeleton height={64} />
        ) : friends.length === 0 ? (
          <div className="text-text-muted text-center py-12 text-sm">{t('friends.empty')}</div>
        ) : (
          friends.map(f => (
            <button
              key={f.user.id}
              onClick={() => f.user.shelf_visibility !== 'hidden' && nav(`/users/${f.user.id}/shelf`)}
              className="w-full p-3 bg-surface rounded-xl flex items-center gap-3 text-left"
            >
              <Avatar user={f.user} size="md" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{displayName(f.user)}</div>
                <div className="text-xs text-text-muted mt-0.5 truncate">
                  {f.is_my_referral && f.invited_by_me
                    ? t('friends.kind_friend_and_referral')
                    : f.is_my_referral
                      ? t('friends.kind_referral')
                      : t('friends.kind_friend')}
                </div>
              </div>
              {f.user.shelf_visibility === 'hidden' ? (
                <IconLock size={16} className="text-text-muted shrink-0" />
              ) : (
                <span className="text-xs text-text-muted flex items-center gap-1 shrink-0">
                  <IconBookmark size={12} />
                  <span>{f.user.bookmarks_count ?? 0}</span>
                </span>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
